import { useRef, useCallback } from 'react';

/**
 * useWebSocket — reusable WebSocket hook for messaging.
 * Handles connection, reconnect (3s), fallback to polling on WS error.
 *
 * @param {object} opts
 *   token         - JWT access token
 *   onMessage     - called with parsed JSON data when ws receives a message
 *   onConnect     - called when connection opens
 *   onDisconnect  - called when connection closes (arg: event)
 *   onPoll        - async fn(convId) used for polling fallback
 *
 * Returns { connectWebSocket, disconnectWebSocket, startPolling }
 */
export function useWebSocket({ token, onMessage, onConnect, onDisconnect, onPoll }) {
  const wsRef            = useRef(null);
  const pollRef          = useRef(null);
  const reconnectRef     = useRef(null);

  const stopAll = useCallback(() => {
    if (wsRef.current)        { wsRef.current.close(1000); wsRef.current = null; }
    if (pollRef.current)      { clearInterval(pollRef.current); pollRef.current = null; }
    if (reconnectRef.current) { clearTimeout(reconnectRef.current); reconnectRef.current = null; }
  }, []);

  const startPolling = useCallback((convId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (!convId || !onPoll) return;
    pollRef.current = setInterval(() => onPoll(convId), 5000);
  }, [onPoll]);

  const connectWebSocket = useCallback((convId) => {
    if (!token || !convId) return;
    stopAll();

    const backendWsUrl = process.env.REACT_APP_BACKEND_URL
      ?.replace('https://', 'wss://')
      ?.replace('http://', 'ws://');
    const wsUrl = `${backendWsUrl}/api/ws/messages/${convId}?token=${token}`;

    try {
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        onConnect?.();
      };

      ws.onmessage = (event) => {
        try { onMessage?.(JSON.parse(event.data), convId); } catch { /* ignore */ }
      };

      ws.onerror = () => {
        onDisconnect?.(null);
        startPolling(convId);
      };

      ws.onclose = (event) => {
        onDisconnect?.(event);
        if (event.code !== 1000 && event.code !== 4001) {
          reconnectRef.current = setTimeout(() => connectWebSocket(convId), 3000);
        } else {
          startPolling(convId);
        }
      };

      wsRef.current = ws;
    } catch {
      startPolling(convId);
    }
  }, [token, onConnect, onMessage, onDisconnect, startPolling, stopAll]); // eslint-disable-line

  return { connectWebSocket, disconnectWebSocket: stopAll, startPolling, pollRef };
}
